import Mention from "@tiptap/extension-mention"
import { PluginKey } from "@tiptap/pm/state"
import {
	NodeViewWrapper,
	ReactNodeViewRenderer,
	ReactRenderer,
} from "@tiptap/react"

import type { ContextMentionListActions } from "./context-mention-list"
import type { NodeViewProps } from "@tiptap/react"
import { contextMentionText } from "./context-mention"
import { contextMentionBadgeClasses } from "./context-mention-badge-classes"
import { ContextMentionIcon } from "./context-mention-icon"
import ContextMentionList from "./context-mention-list"

export const ContextMentionPluginKey = new PluginKey("contextMention")

const ATTRS = [
	"id",
	"type",
	"label",
	"prefix",
	"color",
	"status",
	"icon",
	"imageUrl",
	"origin",
] as const

const ContextMentionBadge = ({ node }: NodeViewProps) => {
	const attrs = node.attrs as {
		type: string
		label: string
		prefix: string | null
		color: string | null
		status: string | null
		icon: string | null
		imageUrl: string | null
		origin: string | null
	}

	return (
		<NodeViewWrapper
			as="span"
			data-context-mention={attrs.type}
			className="inline-flex align-middle outline-none"
		>
			<span
				className={contextMentionBadgeClasses()}
				title={contextMentionText(attrs)}
			>
				<ContextMentionIcon
					item={{
						type: attrs.type as never,
						label: attrs.label,
						color: attrs.color,
						status: attrs.status,
						icon: attrs.icon,
						imageUrl: attrs.imageUrl,
						origin: attrs.origin,
					}}
					className="h-3 w-3 flex-none"
				/>
				{attrs.prefix && (
					<span className="flex-none font-mono opacity-70">{attrs.prefix}</span>
				)}
				<span className="truncate">{attrs.label}</span>
			</span>
		</NodeViewWrapper>
	)
}

export const ContextMention = Mention.extend({
	name: "contextMention",

	addAttributes() {
		return Object.fromEntries(
			ATTRS.map((attr) => [
				attr,
				{
					default: null,
					parseHTML: (element: HTMLElement) =>
						element.getAttribute(`data-${attr}`),
					renderHTML: (attributes: Record<string, unknown>) =>
						attributes[attr] == null
							? {}
							: { [`data-${attr}`]: String(attributes[attr]) },
				},
			]),
		)
	},

	addNodeView() {
		return ReactNodeViewRenderer(ContextMentionBadge)
	},

	renderText({ node }) {
		return contextMentionText(node.attrs as { label: string; prefix: string })
	},
}).configure({
	suggestion: {
		char: "@",
		pluginKey: ContextMentionPluginKey,
		allowSpaces: true,
		command: ({ editor, range, props }) => {
			editor
				.chain()
				.focus()
				.insertContentAt(range, [
					{ type: "contextMention", attrs: props },
					{ type: "text", text: " " },
				])
				.run()
		},
		render: () => {
			let renderer: ReactRenderer<ContextMentionListActions> | null = null

			return {
				onStart: (props) => {
					renderer = new ReactRenderer(ContextMentionList, {
						props,
						editor: props.editor,
					})
				},
				onUpdate: (props) => renderer?.updateProps(props),
				onKeyDown: (props) => {
					if (props.event.key === "Escape") {
						renderer?.destroy()
						renderer = null
						return true
					}
					return renderer?.ref?.onKeyDown(props) ?? false
				},
				onExit: () => {
					renderer?.destroy()
					renderer = null
				},
			}
		},
	},
})
